'use client';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ChevronDown, Plus } from 'lucide-react';
import { ProjectOptions } from '@/lib/services/projects';

interface AssignToProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedProject: string;
  projectOptions: ProjectOptions[];
  onProjectChange: (project: string) => void;
  onAssign: () => void;
  onCreateNewProject: () => void;
  isLoading?: boolean;
  helperMessage?: string;
  title?: string;
  buttonText?: string;
}

export function AssignToProjectModal({
  isOpen,
  onClose,
  selectedProject,
  projectOptions,
  onProjectChange,
  onAssign,
  onCreateNewProject,
  isLoading = false,
  helperMessage,
  title = 'Assign to project',
  buttonText = 'Assign',
}: AssignToProjectModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-gray-900">
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div>
            <label
              htmlFor="project"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Project
            </label>
            <div className="relative">
              <select
                id="project"
                value={selectedProject}
                onChange={(e) => onProjectChange(e.target.value)}
                disabled={isLoading}
                className="w-full h-9 px-3 py-1 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Select</option>
                {projectOptions.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.title}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500 pointer-events-none" />
            </div>
            {helperMessage && (
              <p className="mt-3 text-sm text-gray-600">{helperMessage}</p>
            )}
            <div className="mt-4 flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="text-sm text-gray-500">or</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>
            <div className="mt-4">
              <Button
                variant="outline"
                onClick={onCreateNewProject}
                disabled={isLoading}
                className="w-full h-9 px-3 py-1 text-sm text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                Create a new project
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter className="flex-row justify-end space-x-2 pt-4">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2"
          >
            Cancel
          </Button>
          <Button
            onClick={onAssign}
            disabled={!selectedProject || isLoading}
            className={`px-4 py-2 ${
              selectedProject && !isLoading
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Assigning...
              </div>
            ) : (
              buttonText
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
